import {
  getV1CompaniesCompanyIdDriverDaysDriverDayIdInvoicesPayables,
  getV1CompaniesCompanyIdDriverDaysDriverDayIdInvoicesReceivables,
  getV1CompaniesCompanyIdInvoices,
  GetV1CompaniesCompanyIdInvoicesData,
  getV1CompaniesCompanyIdJobsJobIdPayable,
  getV1CompaniesCompanyIdJobsJobIdReceivable,
  Invoice_Read,
  Invoice_Update,
  InvoiceableType,
  InvoiceCategoryFilter,
  InvoiceState,
  patchV1CompaniesCompanyIdInvoicesId,
  putV1CompaniesCompanyIdInvoicesInvoiceIdApprove,
  putV1CompaniesCompanyIdInvoicesInvoiceIdSendInvoice,
  putV1CompaniesCompanyIdInvoicesInvoiceIdUnapprove,
} from '@treadinc/horizon-api-spec';
import { isAxiosError } from 'axios';
import dayjs from 'dayjs';
import { t } from 'i18next';
import { useCallback, useState } from 'react';

import { EXPORT_ROW_LIMIT } from '~constants/consts';
import useAddOns from '~hooks/useAddOns/useAddOns';
import useInvoiceLineItems from '~hooks/useInvoiceLineItems/useInvoiceLineItems';
import { Invoice } from '~hooks/useInvoices/models';
import { PAY_COMMISSION_PRESENTATION_QUERY_KEY } from '~queries/constants';
import { queryClient } from '~queries/queryClient';
import connection from '~services/connectionModule';
import { extractPagination, PaginationLink } from '~services/pagination';
import { realTimeChannels } from '~services/realTimeChannels';
import { useStores } from '~store';
import { InvoiceSearchParamName } from '~store/InvoicesStore';
import { alert, AlertTypes } from '~types/AlertTypes';
import { downloadCsvFile } from '~utils/csv/csvDownload';
import { exportRowLimitAlert } from '~utils/csv/exportRowLimitAlert';
import { transformInvoicesToCSV } from '~utils/csv/invoiceCsvExport';
import { fetchAllApiPages, TooManyRecordsError } from '~utils/fetchAllPages';
import {
  SentryIgnoreRule,
  withSentryIgnoreRules,
} from '~utils/sentry/sentry-reporting-utils';
import { getEffectiveUserCompanyId } from '~utils/user/user-utils';

// Practical upper bound for client-side CSV generation: keeps the in-memory
// invoice array, the Papa.unparse string, and the browser blob within
// comfortable single-page memory limits while accommodating ~99% of customer
// exports observed in production. Above this, paginated fetches start to be
// noticeably slow on weaker hardware and the exporter falls back to a toast
// asking the user to narrow filters. Bumping this requires re-validating
// download performance and considering a server-side export job.
const useInvoices = () => {
  const [isLoading, setIsLoading] = useState(false);
  const [isUpdating, setIsUpdating] = useState(false);
  const { invoicesStore, toasterStore, userStore } = useStores();
  const { getAllInvoiceLineItems } = useInvoiceLineItems();
  const { getAllInvoiceAddOnCharges } = useAddOns();

  const approveInvoice = async (invoice: Invoice) => {
    const { id, approvable } = invoice;

    if (!approvable) {
      return Promise.reject();
    }

    if (!id) {
      return Promise.reject();
    }

    try {
      setIsUpdating(true);
      const companyId = getEffectiveUserCompanyId(userStore);
      const invoiceId = id;

      const response = await putV1CompaniesCompanyIdInvoicesInvoiceIdApprove({
        path: { 'company-id': companyId, 'invoice-id': invoiceId },
      });

      const updatedInvoice = Invoice.parse(response.data.data);

      invoicesStore.updateInvoice(updatedInvoice);

      return updatedInvoice;
    } catch (error) {
      connection.handleRequestError(
        error,
        t('error_messages.invoices.failed_to_approve'),
      );
      throw error;
    } finally {
      setIsUpdating(false);
    }
  };

  const unapproveInvoice = async (invoice: Invoice) => {
    const { id, unapprovable } = invoice;

    if (!unapprovable) {
      return Promise.reject();
    }

    if (!id) {
      return Promise.reject();
    }

    try {
      setIsUpdating(true);
      const companyId = getEffectiveUserCompanyId(userStore);

      const response = await putV1CompaniesCompanyIdInvoicesInvoiceIdUnapprove({
        path: { 'company-id': companyId, 'invoice-id': id },
      });
      const updatedInvoice = Invoice.parse(response.data.data);

      invoicesStore.updateInvoice(updatedInvoice);

      return updatedInvoice;
    } catch (error) {
      connection.handleRequestError(
        error,
        t('error_messages.invoices.failed_to_unapprove'),
      );
      throw error;
    } finally {
      setIsUpdating(false);
    }
  };

  const updateInvoiceRate = async ({
    invoiceId,
    rate_id,
    rate_type,
    rate_value,
    site_rate_schedule_id,
    vendor_pay_commission_percentage,
    driver_pay_commission_percentage,
  }: {
    vendor_pay_commission_percentage?: Invoice_Update['vendor_pay_commission_percentage'];
    driver_pay_commission_percentage?: Invoice_Update['driver_pay_commission_percentage'];
    invoiceId: string;
    rate_id?: Invoice_Update['rate_id'];
    rate_type?: Invoice_Update['rate_type'];
    rate_value?: Invoice_Update['rate_value'];
    site_rate_schedule_id?: string | null;
  }) => {
    try {
      setIsUpdating(true);
      const companyId = getEffectiveUserCompanyId(userStore);
      const response = await patchV1CompaniesCompanyIdInvoicesId({
        path: { 'company-id': companyId, id: invoiceId },
        body: {
          rate_id,
          rate_type,
          rate_value,
          site_rate_schedule_id,
          vendor_pay_commission_percentage,
          driver_pay_commission_percentage,
        },
      });
      const updatedInvoice = Invoice.parse(response.data.data);
      invoicesStore.updateInvoice(updatedInvoice, true);
    } catch (error) {
      // No 404 allowance: it suppressed both the toast and the Sentry capture, so a rate edit
      // rejected as not-found failed with no feedback at all. Unlike the read paths below, a 404
      // here is never an expected "not ready yet" state.
      connection.handleRequestError(error, t('error_messages.invoices.failed_to_update'));
    } finally {
      setIsUpdating(false);
    }
  };

  const getJobInvoicesByCompanyId = async (companyId: string, link?: PaginationLink) => {
    try {
      setIsLoading(true);

      const query: Partial<GetV1CompaniesCompanyIdInvoicesData['query']> = {
        'page[limit]': invoicesStore.pagination.limit,
        ...invoicesStore.searchParams,
        [InvoiceSearchParamName.INVOICEABLE_TYPE]: InvoiceableType.JOB,
      };

      if (link && invoicesStore.pagination[link]) {
        query[`page[${link}]`] = invoicesStore.pagination[link];
      }

      const response = await getV1CompaniesCompanyIdInvoices({
        path: { 'company-id': companyId },
        query,
      });

      const data = response.data.data;
      const pagination = extractPagination(response);
      const invoices = data.map((invoice) => Invoice.parse(invoice));

      invoicesStore.setInvoices(invoices);
      invoicesStore.setPagination(pagination);
      invoicesStore.updatePageNumber(link);
    } catch (error) {
      connection.handleRequestError(
        error,
        t('error_messages.invoices.failed_to_fetch'),
        [404],
      );
    } finally {
      setIsLoading(false);
    }
  };

  const getDriverDayPayables = async (driverDayId: string) => {
    try {
      setIsLoading(true);
      const companyId = getEffectiveUserCompanyId(userStore);
      const response = await getV1CompaniesCompanyIdDriverDaysDriverDayIdInvoicesPayables(
        {
          path: { 'company-id': companyId, 'driver-day-id': driverDayId },
        },
      );
      const invoices = response.data.data.map((invoice) => Invoice.parse(invoice));
      return invoices;
    } catch (error) {
      connection.handleRequestError(
        error,
        t('error_messages.invoices.failed_to_fetch'),
        [404],
      );
    } finally {
      setIsLoading(false);
    }
  };

  const getDriverDayReceivables = async (driverDayId: string) => {
    try {
      setIsLoading(true);
      const companyId = getEffectiveUserCompanyId(userStore);
      const response =
        await getV1CompaniesCompanyIdDriverDaysDriverDayIdInvoicesReceivables({
          path: { 'company-id': companyId, 'driver-day-id': driverDayId },
        });
      const invoices = response.data.data.map((invoice) => Invoice.parse(invoice));
      return invoices;
    } catch (error) {
      connection.handleRequestError(
        error,
        t('error_messages.invoices.failed_to_fetch'),
        [404],
      );
    } finally {
      setIsLoading(false);
    }
  };

  const sendInvoice = async (invoice: Invoice, companyId: string) => {
    const { id, state } = invoice;

    const isSendable = Boolean(id && state === InvoiceState.APPROVED);

    if (!isSendable) {
      return Promise.reject();
    }

    try {
      setIsUpdating(true);

      const response = await putV1CompaniesCompanyIdInvoicesInvoiceIdSendInvoice({
        path: { 'company-id': companyId, 'invoice-id': String(id) },
      });

      const updatedInvoice = Invoice.parse(response.data.data);

      invoicesStore.updateInvoice(updatedInvoice);

      return updatedInvoice;
    } catch (error) {
      connection.handleRequestError(error, t('error_messages.invoices.failed_to_send'));
    } finally {
      setIsUpdating(false);
    }
  };

  const subscribeToInvoiceRTU = (companyId: string) => {
    const consumer = connection.realTimeConnection;
    const channel = realTimeChannels.InvoiceUpdateChannel;

    let subscription = consumer?.subscriptions.subscriptions.find((sub) => {
      return sub.identifier.includes(channel);
    });

    if (!subscription) {
      subscription = consumer?.subscriptions?.create?.(
        { channel, company_id: companyId },
        {
          connected() {},
          disconnected() {},
          received: ({ data }: { data: Invoice_Read }) => {
            const invoice = Invoice.parse(data);

            invoicesStore.updateInvoice(invoice, true);

            if (invoice.id) {
              getAllInvoiceLineItems(companyId, invoice.id, invoice.materialRateItemized);
              getAllInvoiceAddOnCharges(companyId, invoice.id);
              void queryClient.invalidateQueries({
                queryKey: [PAY_COMMISSION_PRESENTATION_QUERY_KEY, companyId, invoice.id],
              });
            }
          },
        },
      );
    }

    return subscription;
  };

  const getJobPayable = async (
    jobId: string,
    companyId: string,
    options?: { signal?: AbortSignal; skipSentryIgnoreRules?: boolean },
  ) => {
    const { signal, skipSentryIgnoreRules } = options ?? {};
    setIsLoading(true);

    try {
      const fetchPayable = async () => {
        const params = { path: { 'job-id': jobId, 'company-id': companyId } };
        const response = await getV1CompaniesCompanyIdJobsJobIdPayable({
          ...params,
          signal,
        });
        const payable = Invoice.parse(response.data.data);

        return payable;
      };

      let payable: Invoice;

      if (skipSentryIgnoreRules) {
        payable = await fetchPayable();
      } else {
        payable = await withSentryIgnoreRules(
          SentryIgnoreRule.IGNORE_PAYABLE_OR_RECEIVABLE_404,
          fetchPayable,
        );
      }

      return payable;
    } catch (error) {
      // when a payable/receivable is not available yet the API will return a 404.
      // this is to be expected and the error should be addressed in the UI.
      if (isAxiosError(error) && error.response?.status === 404) {
        return null;
      }

      if (signal?.aborted) {
        throw error;
      }

      connection.handleRequestError(
        error,
        t('error_messages.invoices.failed_to_fetch_job_payables'),
      );
    } finally {
      if (!signal?.aborted) {
        setIsLoading(false);
      }
    }
  };

  const getJobReceivable = async (
    jobId: string,
    companyId: string,
    options?: { signal?: AbortSignal; skipSentryIgnoreRules?: boolean },
  ) => {
    const { signal, skipSentryIgnoreRules } = options ?? {};
    setIsLoading(true);

    try {
      const fetchReceivable = async () => {
        const params = { path: { 'job-id': jobId, 'company-id': companyId } };
        const response = await getV1CompaniesCompanyIdJobsJobIdReceivable({
          ...params,
          signal,
        });
        const receivable = Invoice.parse(response.data.data);

        return receivable;
      };

      let receivable: Invoice;

      if (skipSentryIgnoreRules) {
        receivable = await fetchReceivable();
      } else {
        receivable = await withSentryIgnoreRules(
          SentryIgnoreRule.IGNORE_PAYABLE_OR_RECEIVABLE_404,
          fetchReceivable,
        );
      }

      return receivable;
    } catch (error) {
      // when a payable/receivable is not available yet the API will return a 404.
      // this is to be expected and the error should be addressed in the UI.
      if (isAxiosError(error) && error.response?.status === 404) {
        return null;
      }

      if (signal?.aborted) {
        throw error;
      }

      connection.handleRequestError(
        error,
        t('error_messages.invoices.failed_to_fetch_job_receivables'),
      );
    } finally {
      if (!signal?.aborted) {
        setIsLoading(false);
      }
    }
  };

  const exportInvoicesCsv = useCallback(
    async (
      category: InvoiceCategoryFilter,
      filters: Partial<GetV1CompaniesCompanyIdInvoicesData['query']> = {},
    ) => {
      try {
        const companyId = getEffectiveUserCompanyId(userStore);
        const filterQuery: Partial<GetV1CompaniesCompanyIdInvoicesData['query']> = {
          ...filters,
          [InvoiceSearchParamName.INVOICEABLE_TYPE]: InvoiceableType.JOB,
          [InvoiceSearchParamName.INVOICE_CATEGORY]: category,
        };
        const allInvoices = await fetchAllApiPages(
          (query) =>
            getV1CompaniesCompanyIdInvoices({
              path: { 'company-id': companyId },
              query: { ...filterQuery, ...query },
            }),
          Invoice.parse,
          { maxRecords: EXPORT_ROW_LIMIT },
        );

        if (allInvoices.length === 0) {
          toasterStore.push(alert(t('approvals.export.no_data'), AlertTypes.info));
          return;
        }

        const csvData = transformInvoicesToCSV(allInvoices);
        const categoryLabel =
          category === InvoiceCategoryFilter.PAYABLES ? 'payables' : 'receivables';
        const dateForFilename = dayjs().format('YYYY-MM-DD');
        const filename = `${userStore.userCompany.legalName}_${categoryLabel}_${dateForFilename}.csv`;
        downloadCsvFile(csvData, filename);

        toasterStore.push(
          alert(
            t('approvals.export.success', { count: allInvoices.length }),
            AlertTypes.success,
          ),
        );
      } catch (error) {
        if (error instanceof TooManyRecordsError) {
          toasterStore.push(exportRowLimitAlert());

          return;
        }

        connection.handleRequestError(
          error,
          t('error_messages.export.csv_export_failed'),
        );
        throw error;
      }
    },
    [toasterStore, userStore],
  );

  return {
    approveInvoice,
    exportInvoicesCsv,
    getDriverDayPayables,
    getDriverDayReceivables,
    getJobInvoicesByCompanyId,
    getJobPayable,
    getJobReceivable,
    isLoading,
    isUpdating,
    sendInvoice,
    subscribeToInvoiceRTU,
    unapproveInvoice,
    updateInvoiceRate,
  };
};

export default useInvoices;
